import { toast } from "@/components/common/Toast"
import { parseAgentAction } from "@/features/agent-chat/engine/parseAgentAction"
import { AgentAction } from "@/features/agent-chat/types"
import { getAccessToken } from "@/services/api/authSession"
import { fetchWithConnectivity } from "@/services/api/connectivity"
import { transformCitationsFromApi } from "@/services/api/transformCitationsFromApi"
import {
  ChatCompletionResponse,
  ChatCompletionStreamResponse,
  ChatCompletionStreamResponseToolStatus,
  CitationChunkPayload,
  Message,
  SourceMetadata,
} from "@/types/assistantChat"
import { ApiCitation } from "@/types/questionnaire/api"
import { getFlagValue } from "@/utils/featureFlagStore"
import {
  httpRequestErrorFromText,
  limitErrorFromException,
} from "@/utils/limitErrors"
import { moduleTranslate } from "@/utils/moduleTranslate"
import { refreshAfterTrialBlock } from "@/utils/upgradeSession"

const BASE_URL_V1 = (
  import.meta.env.VITE_APP_API_BASE_URL ?? "http://localhost:8000/api/v1"
).replace(/\/api\/v2$/, "/api/v1")
const BASE_URL_V2 = (
  import.meta.env.VITE_APP_API_BASE_URL ?? "http://localhost:8000/api/v2"
).replace(/\/api\/v1$/, "/api/v2")

function getBaseURL(forceV2 = false) {
  return forceV2 || getFlagValue("RAG_V2_CHAT") ? BASE_URL_V2 : BASE_URL_V1
}

interface SendChatOptions {
  webSearchEnabled?: boolean
  conversationId?: string
  signal?: AbortSignal
  forceV2?: boolean
  /** What the user is currently viewing; serialized as `page_context`. */
  pageContext?: { route: string; entities: Record<string, unknown> } | null
}

const apiCalls = {
  sendChatCompletion: async (
    messages: Message[],
    options: SendChatOptions = {},
  ): Promise<ChatCompletionResponse | ReadableStream> => {
    const messagesToSend: Array<{
      role: string
      content: Array<Record<string, unknown>>
    }> = messages.map((message) => {
      // Agent-loop continuation messages carry raw tool blocks; send verbatim.
      if (message.toolContent) {
        return { role: message.role, content: message.toolContent }
      }
      return {
        role: message.role,
        content: [{ text: message.content }],
      }
    })
    const body = JSON.stringify({
      messages: messagesToSend,
      conversation_id: options.conversationId,
      stream: true,
      web_search_enabled: options.webSearchEnabled ?? false,
      ...(options.pageContext
        ? {
            page_context: {
              route: options.pageContext.route,
              entities: options.pageContext.entities,
            },
          }
        : {}),
    })
    // Reads the token on every call, so a retry sends the refreshed one
    const send = async () =>
      fetch(`${getBaseURL(options.forceV2)}/chat/completions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAccessToken({ signal: options.signal })}`,
        },
        signal: options.signal,
        body,
      })
    const failure = async (failed: Response) => {
      const errorText = await failed.text().catch(() => "Unknown error")
      return httpRequestErrorFromText(
        `Failed to send chat completion (${failed.status}): ${errorText}`,
        failed.status,
        errorText,
      )
    }

    let response = await send()
    if (!response.ok || !response.body) {
      let error = await failure(response)
      // A trial 403 can come from a stale token: the user paid but the token
      // still lists the trial group (TRIAL_EXPIRED), or it has no trial clock
      // (TRIAL_STATUS_UNKNOWN). Retry once if a refresh cures the token.
      const trialRefresh = refreshAfterTrialBlock(
        limitErrorFromException(error)?.kind,
      )
      if (trialRefresh && (await trialRefresh)) {
        response = await send()
        if (response.ok && response.body) return response.body
        error = await failure(response)
      }
      throw error
    }

    return response.body
  },

  sendMessages: async (
    messages: Message[],
    options: SendChatOptions = {},
  ): Promise<ChatCompletionResponse | ReadableStream> => {
    return apiCalls.sendChatCompletion(messages, options)
  },

  handleStreamingResponse: async (
    stream: ReadableStream,
    onChunk: (
      content: string | ApiCitation[] | CitationChunkPayload,
      role?: "assistant" | "system" | "citation" | "reasoning",
    ) => void,
    onDone: () => void,
    onConversationId?: (conversationId: string) => void,
    onAction?: (action: AgentAction) => void,
    onToolStatus?: (status: ChatCompletionStreamResponseToolStatus) => void,
  ) => {
    const reader = stream.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    let messageRole: "assistant" | "system" | "citation" | undefined

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          onDone()
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split("\n")
        buffer = lines.pop() || ""

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(5).trim()
            if (data === "[DONE]") {
              onDone()
              return
            }
            try {
              const chunkResponse:
                | ChatCompletionStreamResponse
                | ChatCompletionStreamResponseToolStatus = JSON.parse(data)
              if (chunkResponse.object === "chat.completion.tool_status") {
                onToolStatus?.(chunkResponse)
                continue
              }
              if (chunkResponse.object === "chat.completion.chunk") {
                const delta = chunkResponse.choices![0].delta
                if (delta.role) {
                  messageRole = delta.role
                }
                if (delta.reasoning) {
                  onChunk(delta.reasoning, "reasoning")
                }
                if (delta.content) {
                  onChunk(delta.content, messageRole)
                }
              }
              if (chunkResponse.object === "chat.completion.citations") {
                // Transform snake_case API response to camelCase for frontend
                const rawCitations = chunkResponse.citations as unknown[]
                const transformedCitations =
                  transformCitationsFromApi(rawCitations)
                const payload: CitationChunkPayload = {
                  citations: transformedCitations,
                  metadata: normalizeSourceMetadata(
                    chunkResponse.source_metadata as RawSourceMetadata,
                  ),
                  toolCallId: chunkResponse.tool_call_id,
                }
                onChunk(payload, "citation")
              }
              if (
                chunkResponse.object === "chat.conversation" &&
                chunkResponse.conversation_id
              ) {
                onConversationId?.(chunkResponse.conversation_id)
              }
              if (
                (chunkResponse as { object?: string }).object ===
                "chat.completion.action"
              ) {
                const action = parseAgentAction(
                  (chunkResponse as { action?: unknown }).action,
                )
                if (action) {
                  onAction?.(action)
                }
              }
            } catch (e) {
              console.error("Error parsing streaming response:", e)
              toast.error(moduleTranslate("CHAT/ERROR_STREAM_PARSE"))
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }
  },
}

export const { sendChatCompletion, sendMessages, handleStreamingResponse } =
  apiCalls

export type RawSourceMetadata = Partial<SourceMetadata> & {
  web_search_enabled?: boolean
  web_search_applied?: boolean
  web_results_count?: number
  workspace_results_count?: number
  web_search_notice?: string | null
}

export function normalizeSourceMetadata(
  raw?: RawSourceMetadata | null,
): SourceMetadata | undefined {
  if (!raw) {
    return undefined
  }

  return {
    webSearchEnabled: raw.webSearchEnabled ?? raw.web_search_enabled ?? false,
    webSearchApplied: raw.webSearchApplied ?? raw.web_search_applied ?? false,
    webResultsCount: raw.webResultsCount ?? raw.web_results_count ?? 0,
    workspaceResultsCount:
      raw.workspaceResultsCount ?? raw.workspace_results_count ?? 0,
    webSearchNotice: raw.webSearchNotice ?? raw.web_search_notice ?? undefined,
  }
}

export async function getRemainingChatMessages(): Promise<{
  remaining_messages: number | null
  used_messages: number
  is_unlimited: boolean
}> {
  const token = await getAccessToken()
  // Usage endpoint only exists on v1 — always use BASE_URL_V1 regardless of toggle
  const response = await fetchWithConnectivity(
    `${BASE_URL_V1}/usage/chat/remaining`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error("Failed to fetch remaining messages")
  }

  return response.json()
}
